import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {Maintenance} from '@domain/planning-prototype/maintenance';
import {MaintenanceType} from '@domain/planning-prototype/maintenance-type';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {AssertionUtils, BaseComponent, MathUtils} from '@vdw/angular-component-library';
import {DateTime} from 'luxon';
import moment from 'moment';
import {filter, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';
import {PlanningItemSidebarComponent} from '../planning-item-sidebar/planning-item-sidebar.component';

@Component({
  selector: 'app-maintenance-sidebar',
  templateUrl: './maintenance-sidebar.component.html'
})
export class MaintenanceSidebarComponent extends BaseComponent implements OnInit, OnDestroy {
  public maintenanceForm = new FormGroup({
    type: new FormControl(MaintenanceType.MAINTENANCE),
    earliestStartDate: new FormControl<DateTime>(null),
    minimumDuration: new FormControl<number>(null, [Validators.min(0)])
  });

  public maintenanceTypes = [MaintenanceType.MAINTENANCE, MaintenanceType.MACHINE_UNAVAILABLE];
  public createItemMode = false;
  public maintenance: Maintenance;

  private initialType: MaintenanceType;

  public constructor(
    private readonly parent: PlanningItemSidebarComponent,
    private readonly planningContext: PlanningPrototypeContextService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.resetFormWhenSelectedItemChanges();

    this.updateMaintenanceIconWhenSelectedTypeChanges();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.maintenance.maintenanceType = this.initialType;
  }

  public saveChanges(): void {
    this.planningContext.selectItem(null);
    if (!this.maintenanceForm.dirty) {
      return;
    }
    this.maintenance.maintenanceType = this.maintenanceForm.controls.type.value;
    this.initialType = this.maintenance.maintenanceType;
    this.saveDateInfo();
    this.planningContext.markAsChanged(this.parent.machine);
  }

  public delete(): void {
    this.planningContext.delete(this.maintenance, this.planningContext.planningLinesByMachine[this.parent.machine.id].planningItems, this.parent.machine);
  }

  public createItem(): void {
    this.planningContext.selectItem(null);
    this.planningContext.itemCreationPlaceholder = null;
    this.saveDateInfo();
    this.initialType = this.maintenance.maintenanceType;
    this.planningContext.addItemToList(
      this.maintenance,
      this.planningContext.planningLinesByMachine[this.parent.machine.id].planningItems,
      this.planningContext.sortedPlanningItemForecasts[this.maintenance.draftId].estimatedStart
    );
    this.planningContext.markAsChanged(this.parent.machine);
  }

  private saveDateInfo(): void {
    this.maintenance.earliestStartDate = this.maintenanceForm.controls.earliestStartDate.value?.toJSDate();
    this.maintenance.minimumDuration = moment.duration(this.maintenanceForm.controls.minimumDuration.value, 'hours').format('d.hh:mm:ss', {trim: 'both', stopTrim: 'h m s'});
  }

  private resetForm(maintenance: Maintenance): void {
    if (!AssertionUtils.isNullOrUndefined(this.maintenance)) {
      this.maintenance.maintenanceType = this.initialType;
    }
    this.initialType = maintenance.maintenanceType;
    this.maintenance = maintenance;
    this.createItemMode = this.planningContext.itemCreationPlaceholder === maintenance;
    this.maintenanceForm.reset({
      type: maintenance.maintenanceType,
      earliestStartDate: maintenance.earliestStartDate ? DateTime.fromJSDate(maintenance.earliestStartDate) : null,
      minimumDuration: MathUtils.round(moment.duration(maintenance.minimumDuration).asHours(), 2)
    });
    this.maintenanceTypes = PlanningItemHelper.getMaintenanceTypesForMachineType(this.parent.machine.equipmentKind);
  }

  private resetFormWhenSelectedItemChanges(): void {
    this.planningContext.selectedItemChanges
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((item: PlanningItem) => item instanceof Maintenance)
      )
      .subscribe((maintenance: Maintenance) => this.resetForm(maintenance));
  }

  private updateMaintenanceIconWhenSelectedTypeChanges(): void {
    this.maintenanceForm.controls.type.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((value: MaintenanceType) => {
      this.maintenance.maintenanceType = value;
    });
  }
}
